import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  ArrowLeft, 
  Printer,
  PencilSimple,
  Trash,
  FirstAidKit,
  Heartbeat,
  ClipboardText,
  ShieldCheck,
  LockSimple,
  FilePdf,
  ShieldWarning,
  IdentificationCard,
  Pill
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LOGO_1 = "https://customer-assets.emergentagent.com/job_medform-standby/artifacts/3xn9wu25_standby_transparent.png";
const LOGO_2 = "https://customer-assets.emergentagent.com/job_medform-standby/artifacts/v4idzbss_on_site_transparent.png";

const TAG_COLORS = {
  Green: 'bg-green-100 text-green-800 border-green-300',
  Yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  Orange: 'bg-orange-100 text-orange-800 border-orange-300',
  Red: 'bg-red-100 text-red-800 border-red-300',
  Black: 'bg-gray-800 text-gray-100 border-gray-900'
};

export default function PatientViewPage() {
  const { id } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [auditTrail, setAuditTrail] = useState([]);
  const [idImages, setIdImages] = useState([]);

  const fetchPatient = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/patients/${id}`);
      setPatient(data);
    } catch (err) {
      console.warn('Patient fetch failed:', err.message);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  const fetchAuditTrail = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/patients/${id}/audit`);
      setAuditTrail(data);
    } catch (err) {
      console.warn('Audit trail fetch failed (non-critical):', err.message);
    }
  }, [id, token]);

  const fetchIdImages = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/patients/${id}/id-images`);
      setIdImages(data);
    } catch { /* non-critical */ }
  }, [id]);

  useEffect(() => {
    fetchPatient();
    fetchAuditTrail();
    fetchIdImages();
  }, [fetchPatient, fetchAuditTrail, fetchIdImages]);

  const handlePrint = () => {
    window.print();
  };

  const [showPdfDialog, setShowPdfDialog] = useState(false);

  const handleDownloadPDF = async (redacted = false) => {
    setShowPdfDialog(false);
    try {
      const response = await axios.get(`${API_URL}/api/patients/${id}/pdf?redacted=${redacted}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const suffix = redacted ? '_redacted' : '';
      link.setAttribute('download', `patient_${patient?.form_number || id}${suffix}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`PDF downloaded${redacted ? ' (redacted)' : ''}`);
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const getSignatureStatusLabel = (status, hasLegacySignature) => {
    switch (status) {
      case 'SIGNED': return 'SIGNED';
      case 'REFUSE_TO_SIGN': return 'REFUSE TO SIGN';
      case 'UNABLE_TO_SIGN': return 'UNABLE TO SIGN DUE TO MEDICAL ISSUES';
      case 'MINOR': return 'Minor';
      default: return hasLegacySignature ? 'Signed (Legacy)' : 'Not provided';
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/patients/${id}`, {});
      toast.success('Patient record deleted');
      navigate('/patients');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete patient');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleCloseForm = async () => {
    if (!window.confirm('Are you sure you want to close this form? This action cannot be undone.')) return;
    try {
      await axios.post(`${API_URL}/api/patients/${id}/close`, {}, {});
      toast.success('Form closed successfully');
      fetchPatient();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close form');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <p className="text-slate-500">Loading patient record...</p>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <p className="text-slate-500 mb-4">Patient record not found.</p>
            <Link to="/patients">
              <Button variant="outline">Back to Patients</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pmhx = patient.past_medical_history || {};
  const vitals1 = patient.vital_signs_1 || {};
  const vitals2 = patient.vital_signs_2 || {};
  const vitals3 = patient.vital_signs_3 || {};
  const vitals4 = patient.vital_signs_4 || {};

  return (
    <div className="min-h-screen bg-slate-100" data-testid="patient-view-page">
      {/* Header - Hidden on print */}
      <header className="bg-white border-b border-slate-200 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/patients" data-testid="back-to-patients">
                <Button variant="ghost" size="sm" className="rounded-sm">
                  <ArrowLeft size={18} className="mr-2" />
                  Back to Patients
                </Button>
              </Link>
              <div className="h-6 w-px bg-slate-200" />
              <div className="flex items-center gap-3">
                <img src={LOGO_1} alt="Logo 1" className="h-8 object-contain" />
                <img src={LOGO_2} alt="Logo 2" className="h-8 object-contain" />
              </div>
            </div>
            <div className="flex items-center gap-4">
              {(patient.form_status !== 'closed' || user?.role === 'admin') && (
                  <Link to={`/patient/${id}/edit`}>
                    <Button 
                      variant="outline" 
                      className="rounded-sm"
                      data-testid="edit-patient-button"
                    >
                      <PencilSimple size={18} className="mr-2" />
                      {patient.form_status === 'closed' ? 'Amend' : 'Edit'}
                    </Button>
                  </Link>
              )}
              {patient.form_status !== 'closed' && (
                  <Button
                    variant="outline"
                    className="rounded-sm border-orange-300 text-orange-700 hover:bg-orange-50"
                    onClick={handleCloseForm}
                    data-testid="close-form-button"
                  >
                    <LockSimple size={18} className="mr-2" />
                    Close Form
                  </Button>
              )}
              {user?.role === 'admin' && (
                <Button 
                  variant="outline" 
                  className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => setShowDeleteDialog(true)}
                  data-testid="delete-patient-button"
                >
                  <Trash size={18} className="mr-2" />
                  Delete
                </Button>
              )}
              <Button 
                variant="outline" 
                className="rounded-sm"
                onClick={() => setShowPdfDialog(true)}
                data-testid="download-pdf-button"
              >
                <FilePdf size={18} className="mr-2" />
                Download PDF
              </Button>
              <Button 
                variant="outline" 
                className="rounded-sm"
                onClick={handlePrint}
                data-testid="print-patient-button"
              >
                <Printer size={18} className="mr-2" />
                Print Record
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Print Header - Only visible on print */}
      <div className="hidden print:block print:mb-6">
        <div className="flex items-center justify-between border-b pb-4">
          <div className="flex items-center gap-4">
            <img src={LOGO_1} alt="Logo 1" className="h-12 object-contain" />
            <img src={LOGO_2} alt="Logo 2" className="h-12 object-contain" />
          </div>
          <div className="text-right">
            <h2 className="font-bold">Patient Assessment Record</h2>
            <p className="text-sm text-slate-500">
              Printed: {new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print-container">
        {/* Patient Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold text-slate-900">
                {patient.first_name} {patient.last_name}
              </h1>
              {patient.form_type === 'medication' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-700" data-testid="form-type-badge">
                  <Pill size={12} /> OTC MED
                </span>
              )}
              {patient.form_status === 'closed' ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-red-100 text-red-700" data-testid="form-status-badge">
                  <LockSimple size={12} /> CLOSED
                </span>
              ) : (
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-700" data-testid="form-status-badge">
                  OPEN
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-slate-500 text-sm mt-1">
              <span className="font-mono font-semibold bg-slate-100 px-2 py-0.5 rounded" data-testid="form-number-display">
                Form #{patient.form_number || 'N/A'}
              </span>
              <span>Created: {new Date(patient.created_at).toLocaleString('en-US', { timeZone: 'America/New_York' })}</span>
            </div>
          </div>
          <span className={`inline-flex px-4 py-2 text-sm font-bold rounded border-2 ${TAG_COLORS[patient.tag_color] || 'bg-slate-100 text-slate-600'}`}>
            Triage: {patient.tag_color}
          </span>
        </div>

        <div className="space-y-6">
          {/* Patient Information */}
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <FirstAidKit size={20} className="text-blue-600" />
                Patient Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Form Date</p>
                  <p className="font-medium">{patient.form_date || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Event Name</p>
                  <p className="font-medium">{patient.event_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Date of Birth</p>
                  <p className="font-medium">{patient.dob || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Gender</p>
                  <p className="font-medium">{patient.gender || 'N/A'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs uppercase tracking-wider text-slate-500">Address</p>
                  <p className="font-medium">{patient.address || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Telephone</p>
                  <p className="font-medium">{patient.tel || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Triage Center</p>
                  <p className="font-medium">{patient.triage_center || 'Unassigned'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Patient Contact Time</p>
                  <p className="font-medium">{patient.patient_contact_time || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Scanned ID Images */}
          {idImages.length > 0 && (
            <Card className="border-slate-200 no-print" data-testid="id-images-section">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <IdentificationCard size={20} className="text-blue-600" />
                  Scanned Identification
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 flex-wrap">
                  {idImages.map((img, i) => (
                    <div key={i} className="border border-slate-200 rounded-sm overflow-hidden">
                      <img
                        src={`data:image/jpeg;base64,${img.image_base64}`}
                        alt={`ID ${img.side}`}
                        className="w-64 h-40 object-cover"
                      />
                      <div className="px-3 py-2 bg-slate-50 text-xs text-slate-500 capitalize">
                        {img.doc_type?.replace('_', ' ')} — {img.side} | Scanned: {img.scanned_at ? new Date(img.scanned_at).toLocaleString('en-US', { timeZone: 'America/New_York' }) : 'N/A'}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Assessment Details */}
          {(!patient.form_type || patient.form_type === 'acr') && (
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardText size={20} className="text-green-600" />
                Assessment Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Reason for Treatment</p>
                  <p className="font-medium">{patient.reason_for_treatment}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Triage Color</p>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded border ${TAG_COLORS[patient.tag_color]}`}>
                    {patient.tag_color}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Medical History */}
          {(!patient.form_type || patient.form_type === 'acr') && (
          <>
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Heartbeat size={20} className="text-red-600" />
                Medical History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Allergies</p>
                  {patient.allergies_nka ? (
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700">No Known Allergies (NKA)</span>
                  ) : patient.allergies_unable ? (
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded bg-amber-100 text-amber-700">Unable to Obtain</span>
                  ) : (
                    <p className="font-medium">{patient.allergies || 'None reported'}</p>
                  )}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Current Medication</p>
                  {patient.medication_none ? (
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700">No Current Medication</span>
                  ) : patient.medication_unable ? (
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded bg-amber-100 text-amber-700">Unable to Obtain</span>
                  ) : (
                    <p className="font-medium">{patient.medication || 'None reported'}</p>
                  )}
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Past Medical History (PMHx)</p>
                <div className="flex flex-wrap gap-2">
                  {pmhx.asthma && <span className="px-2 py-1 bg-slate-100 rounded text-sm">Asthma</span>}
                  {pmhx.diabetes && <span className="px-2 py-1 bg-slate-100 rounded text-sm">Diabetes</span>}
                  {pmhx.cardiac && <span className="px-2 py-1 bg-slate-100 rounded text-sm">Cardiac</span>}
                  {pmhx.hypertension && <span className="px-2 py-1 bg-slate-100 rounded text-sm">Hypertension</span>}
                  {pmhx.cardiac_hypertension && !pmhx.cardiac && <span className="px-2 py-1 bg-slate-100 rounded text-sm">Cardiac/Hypertension</span>}
                  {pmhx.copd && <span className="px-2 py-1 bg-slate-100 rounded text-sm">COPD</span>}
                  {pmhx.emphysema && <span className="px-2 py-1 bg-slate-100 rounded text-sm">Emphysema</span>}
                  {pmhx.psychiatric && <span className="px-2 py-1 bg-slate-100 rounded text-sm">Psychiatric</span>}
                  {pmhx.other && <span className="px-2 py-1 bg-slate-100 rounded text-sm">{pmhx.other}</span>}
                  {!pmhx.asthma && !pmhx.diabetes && !pmhx.cardiac && !pmhx.hypertension && !pmhx.cardiac_hypertension && !pmhx.copd && !pmhx.emphysema && !pmhx.psychiatric && !pmhx.other && (
                    <span className="text-slate-500 text-sm">None reported</span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Vital Signs */}
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Heartbeat size={20} className="text-purple-600" />
                Vital Signs
                {patient.vitals_na && <span className="text-sm font-normal text-slate-500 ml-2">(N/A - No vitals taken)</span>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {patient.vitals_na ? (
                <p className="text-sm text-slate-500 italic">No vitals were taken for this patient.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    { label: 'Set 1', data: vitals1 },
                    { label: 'Set 2', data: vitals2 },
                    { label: 'Set 3', data: vitals3 },
                    { label: 'Set 4', data: vitals4 },
                  ].map((set) => (
                    <div key={set.label} className="bg-slate-50 p-4 rounded">
                      <h4 className="font-semibold text-slate-700 mb-3">{set.label}</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><span className="text-slate-500">Time:</span> {set.data.time || 'N/A'}</div>
                        <div><span className="text-slate-500">Pulse:</span> {set.data.pulse || 'N/A'}</div>
                        <div><span className="text-slate-500">BP:</span> {set.data.blood_pressure || 'N/A'}</div>
                        <div><span className="text-slate-500">Resp:</span> {set.data.respiration || 'N/A'}</div>
                        <div><span className="text-slate-500">O2 Sat:</span> {set.data.o2_saturation || 'N/A'}</div>
                        <div><span className="text-slate-500">CTC:</span> {set.data.ctc || 'N/A'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Physical Assessment */}
          {patient.physical_assessment && (
            <Card className="border-slate-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Physical Assessment & Findings</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-700 bg-slate-50 p-4 rounded whitespace-pre-wrap">
                  {patient.physical_assessment}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Transport Status */}
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Transport Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <span className={`px-3 py-2 rounded font-medium ${
                  patient.is_transported 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-slate-100 text-slate-600'
                }`}>
                  {patient.is_transported ? 'Patient Transported' : 'Patient Not Transported'}
                </span>
                {patient.is_transported && patient.transported_by && (
                  <span className="text-slate-600">
                    By: <strong>{patient.transported_by}</strong>
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Signatures */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* HIPAA Signature */}
            <Card className="border-slate-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShieldCheck size={20} className="text-green-600" />
                  HIPAA Consent
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Signature Status</p>
                  <p className="font-medium">{getSignatureStatusLabel(patient.hipaa_signature_status, patient.hipaa_signature)}</p>
                </div>
                {patient.hipaa_signature && (
                  <div>
                    <img 
                      src={patient.hipaa_signature} 
                      alt="HIPAA Signature" 
                      className="border border-slate-200 rounded bg-white max-h-24"
                    />
                  </div>
                )}
                {patient.hipaa_signature_status === 'MINOR' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 space-y-2">
                    <p className="text-xs uppercase tracking-wider text-amber-700 font-semibold">Guardian Info</p>
                    <p className="text-sm"><strong>Name:</strong> {patient.hipaa_guardian_name || 'N/A'}</p>
                    {patient.hipaa_guardian_signature && (
                      <img src={patient.hipaa_guardian_signature} alt="Guardian Signature" className="border border-amber-200 rounded bg-white max-h-20" />
                    )}
                  </div>
                )}
                <p className="text-sm text-slate-500">Date: {patient.hipaa_date || 'N/A'}</p>
              </CardContent>
            </Card>

            {/* RMA Signature */}
            <Card className="border-slate-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShieldCheck size={20} className="text-orange-600" />
                  RMA / Transport Consent
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Signature Status</p>
                  <p className="font-medium">{getSignatureStatusLabel(patient.rma_signature_status, patient.rma_signature)}</p>
                </div>
                {patient.rma_signature && (
                  <div>
                    <img 
                      src={patient.rma_signature} 
                      alt="RMA Signature" 
                      className="border border-slate-200 rounded bg-white max-h-24"
                    />
                  </div>
                )}
                {patient.rma_signature_status === 'MINOR' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 space-y-2">
                    <p className="text-xs uppercase tracking-wider text-amber-700 font-semibold">Guardian Info</p>
                    <p className="text-sm"><strong>Name:</strong> {patient.rma_guardian_name || 'N/A'}</p>
                    {patient.rma_guardian_signature && (
                      <img src={patient.rma_guardian_signature} alt="Guardian Signature" className="border border-amber-200 rounded bg-white max-h-20" />
                    )}
                  </div>
                )}
                <p className="text-sm text-slate-500">Date: {patient.rma_date || 'N/A'}</p>
              </CardContent>
            </Card>
          </div>

          {/* Provider Signature */}
          {(patient.provider_name || patient.provider_signature) && (
            <Card className="border-slate-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ClipboardText size={20} className="text-teal-600" />
                  Provider Signature
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Provider Name</p>
                    <p className="font-medium">{patient.provider_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Level of Certification</p>
                    <p className="font-medium">{patient.provider_certification || 'N/A'}</p>
                  </div>
                </div>
                {patient.provider_signature && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Signature</p>
                    <img 
                      src={patient.provider_signature} 
                      alt="Provider Signature" 
                      className="border border-slate-200 rounded bg-white max-h-24"
                      data-testid="provider-signature-view"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          </>
          )}

          {/* OTC Medication Details - shown for medication form type */}
          {patient.form_type === 'medication' && (
          <>
            <Card className="border-slate-200" data-testid="otc-medication-view">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Pill size={20} className="text-green-600" />
                  OTC Medication Provided
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Medications</p>
                  <div className="flex flex-wrap gap-2">
                    {(patient.otc_medications_provided || []).map((med) => (
                      <span key={med} className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded border border-blue-200 text-sm font-medium capitalize">
                        {med === 'tylenol' ? 'Tylenol (Acetaminophen)' :
                         med === 'aspirin' ? 'Aspirin (ASA)' :
                         med === 'motrin' ? 'Motrin (Ibuprofen)' :
                         med === 'advil' ? 'Advil (Ibuprofen)' : med}
                      </span>
                    ))}
                    {(!patient.otc_medications_provided || patient.otc_medications_provided.length === 0) && (
                      <span className="text-slate-500 text-sm">None selected</span>
                    )}
                  </div>
                </div>
                {patient.otc_other_medication && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Other Medication</p>
                    <p className="font-medium">{patient.otc_other_medication}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Allergies</p>
                  <p className="font-medium">{patient.allergies || 'None reported'}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200" data-testid="otc-acknowledgment-view">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShieldCheck size={20} className="text-amber-600" />
                  OTC Acknowledgment & Release
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Agreement Status</p>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${patient.otc_agreed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {patient.otc_agreed ? 'Agreed to Terms' : 'Not Agreed'}
                  </span>
                </div>
                {patient.otc_signature && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Signature</p>
                    <img 
                      src={patient.otc_signature} 
                      alt="OTC Signature" 
                      className="border border-slate-200 rounded bg-white max-h-24"
                      data-testid="otc-signature-view"
                    />
                  </div>
                )}
                <p className="text-sm text-slate-500">Date: {patient.otc_date || 'N/A'}</p>
              </CardContent>
            </Card>
          </>
          )}

          {/* Record Info */}
          <div className="text-center text-sm text-slate-400 pt-4 border-t border-slate-200">
            Record created by: {patient.created_by_name || 'Unknown'} | 
            ID: {patient.id}
          </div>

          {/* Audit Trail */}
          {auditTrail.length > 0 && (
            <Card className="border-slate-200" data-testid="audit-trail-card">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ClipboardText size={20} className="text-indigo-600" />
                  Form Audit Trail
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {auditTrail.map((entry, idx) => (
                    <div key={`audit-${entry.timestamp}-${idx}`} className="flex items-start gap-3 text-sm" data-testid={`audit-entry-${idx}`}>
                      <div className="w-2 h-2 mt-1.5 rounded-full bg-indigo-400 shrink-0" />
                      <div>
                        <span className="font-medium text-slate-800 capitalize">{entry.action?.replace(/_/g, ' ')}</span>
                        <span className="text-slate-500"> by </span>
                        <span className="font-medium text-slate-700">{entry.user_name}</span>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {new Date(entry.timestamp).toLocaleString('en-US', { timeZone: 'America/New_York' })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Patient Record</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the record for <strong>{patient.first_name} {patient.last_name}</strong>? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
              data-testid="confirm-delete-button"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PDF Redaction Choice Dialog */}
      <Dialog open={showPdfDialog} onOpenChange={setShowPdfDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Download PDF</DialogTitle>
            <DialogDescription>
              Choose whether to include or redact protected health information (DOB, address, signatures, medical problems).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              className="rounded-sm flex-1"
              onClick={() => handleDownloadPDF(false)}
              data-testid="pdf-unredacted-button"
            >
              <FilePdf size={18} className="mr-2" />
              Full (Unredacted)
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white rounded-sm flex-1"
              onClick={() => handleDownloadPDF(true)}
              data-testid="pdf-redacted-button"
            >
              <ShieldWarning size={18} className="mr-2" />
              Redacted Copy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
